<script lang="ts">
  import type { PortableTextSpan } from '@portabletext/types';
  import type { MarkComponentProps } from '@portabletext/svelte';
  import type { Modal as ModalSchemaType } from '$lib/sanity/schemas/components/Modal';
  import { modals } from '$lib/stores/modals';
  import { flagValue } from '$lib/context/flags';
  import { anchorNavigationStore, type AnchorNavigationStore } from '$lib/stores/anchorNavigation';
  import { linkPagePath } from '$lib/utils/routes';
  import type { LinkPage } from '$lib/sanity/schemas';
  import { FEATURE_FLAGS } from '$lib/flags/constants';

  // Property custom marks receive from @portabletext/svelte when redered
  export let portableText: MarkComponentProps<{
    _key: string;
    href?: string;
    url?: string;
    page?: LinkPage;
    type?: string;
    anchorKey?: string;
    modal?: ModalSchemaType;
  }>;

  let modalBtn: HTMLElement;
  let linkEl: HTMLAnchorElement;

  /**
   * Returns true if child portable text span is wrapped with targetMark mark.
   */
  function childHasMark(targetMark: string): boolean {
    const linkWrappedSpan = portableText.parentBlock?.children
      .filter((block) => block._type === 'span')
      .find((block): block is PortableTextSpan => {
        return block.marks?.includes(portableText.value._key) ?? false;
      });

    return linkWrappedSpan?.marks?.includes(targetMark) ?? false;
  }

  function useAnchorId(anchorNavigationStore: AnchorNavigationStore, anchorKey: string) {
    const anchor = anchorNavigationStore.anchors.find(
      (anchor) => anchor.markKey === anchorKey && !!anchor.el.offsetParent
    );

    return anchor?.el.getAttribute('id');
  }

  $: pageUrl = portableText.value.page && linkPagePath(portableText.value.page);
  // the property representing regular links was formerly named url but has been changed to href
  // sanity uses href and represents pasted links with the href field, so that will be the field from here
  // when text with links are pasted into sanity, it will not have a a type field, so by default this will be interpreted as a link
  $: href =
    portableText.value.type === 'url' || !portableText.value.type
      ? portableText.value.href || portableText.value.url
      : pageUrl;
  $: isRelativeLink = href?.startsWith('/');

  $: anchorId =
    portableText.value.anchorKey &&
    useAnchorId($anchorNavigationStore, portableText.value.anchorKey);
  $: isAnchorLink = portableText.value.type === 'anchor' && !!anchorId;

  $: hasSubscript = childHasMark('subScript');
  $: hasSuperscript = childHasMark('superScript');

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);

  function handleClick(e: MouseEvent) {
    if (!isAnchorLink || !anchorId) {
      return;
    }

    e.preventDefault();

    const possibleTargets = document.querySelectorAll<HTMLAnchorElement>(`#${anchorId}`);
    const target = Array.from(possibleTargets).find((element) => !!element.offsetParent);

    if (target) {
      const { top } = target.getBoundingClientRect();

      window.gtag &&
        window.gtag('event', 'anchor_click', {
          event_category: 'Anchor Links',
          event_label: anchorId
        });

      window.scrollTo({
        top: top + window.scrollY - $anchorNavigationStore.offset,
        behavior: 'smooth'
      });
    }

    linkEl.blur();
  }

  function handleOpenModal() {
    if (portableText.value.modal) {
      modals.open(portableText.value.modal._id, modalBtn);
    }
  }

  function handleOpenModalKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleOpenModal();
    }
  }
</script>

{#if href || isAnchorLink}
  <a
    bind:this={linkEl}
    class:sub={hasSubscript}
    class:sup={hasSuperscript}
    class:reskin={brandReskinEnabled}
    href={isAnchorLink ? `#${anchorId}` : href}
    target={isAnchorLink || isRelativeLink ? '' : '_blank'}
    on:click|stopPropagation={handleClick}
  >
    <slot />
  </a>
{:else if portableText.value.type === 'modal'}
  <span
    class="modal-trigger"
    class:reskin={brandReskinEnabled}
    role="button"
    tabindex="0"
    bind:this={modalBtn}
    on:click|stopPropagation={handleOpenModal}
    on:keydown={handleOpenModalKeydown}
  >
    <slot />
  </span>
{:else}
  <slot />
{/if}

<style lang="scss">
  a,
  .modal-trigger {
    word-break: break-word;
    color: var(--theme-link-text-color, inherit);
    opacity: var(--theme-link-text-opacity, 1);
    text-transform: var(--theme-link-text-transform, none);
    transition: color 0.2s ease;
    text-decoration: underline;
    cursor: pointer;

    &.reskin {
      font-weight: 500;
    }

    &:focus,
    &:focus-visible,
    &:hover {
      color: var(--theme-link-hover-focus-color, var(--twist-color-gray-0));
      opacity: var(--theme-link-hover-focus-opacity, 1);
    }

    &:active {
      color: var(
        --theme-link-active-color,
        var(--theme-link-hover-focus-color, var(--twist-color-gray-0))
      );
      opacity: var(--theme-link-active-opacity, 0.6);
    }

    &.reskin {
      &:focus,
      &:focus-visible,
      &:hover,
      &:active {
        color: var(--theme-link-hover-focus-color, #666666);
        opacity: 1;
      }
    }
  }

  a {
    &.sup,
    &.sub {
      text-decoration: none;

      :global(sub),
      :global(sup) {
        text-decoration: underline;
      }
    }
  }
</style>
